import { PositionalAudio } from "@react-three/drei";
import { PointLightProps, useFrame, Vector3 } from "@react-three/fiber";
import { Howl } from "howler";
import React, {
  MutableRefObject,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import { AppContext } from "./App";
import angleBetweenPoints from "./utility_functions/AngleBetweenPoints";
import dist from "./utility_functions/Dist";

type Props = {
  position: Vector3;
  color: string;
  sound: Howl;
  detail: number;
};

export default function MusicNode({ position, color, sound, detail }: Props) {
  const { playerRef, masterVolumeRef } = useContext(AppContext);
  const pointsMaterialRef = useRef<any>();
  const pointsRef = useRef<any>();
  const lineRef = useRef<any>();
  const lineMaterialRef = useRef<any>();

  const minVolumeDistance = 25;
  const maxVolumeDistance = 4;
  const minVolume = 0;
  const maxVolume = 0.8;
  const panFactor = 0.8;

  const minOpacity = 0.03;
  const maxOpacity = 1;

  const minSize = 0.8;
  const maxSize = 4;

  const minPointSize = 0.1;
  const maxPointSize = 0.3;

  const minRotationSpeed = 0.01;
  const maxRotationSpeed = 0.02;

  function getIntensity(
    distance: number,
    maxDist: number,
    minDist: number,
    maxOut: number,
    minOut: number
  ) {
    if (distance > minDist) {
      return minOut;
    }
    if (distance < maxDist) {
      return maxOut;
    }

    const ratio = 1 - (distance - maxDist) / (minDist - maxDist);
    return minOut + (maxOut - minOut) * ratio;
  }

  useFrame(() => {
    const distance = dist(
      { x: playerRef.current.position.x, y: playerRef.current.position.z },
      { x: position[0], y: position[2] }
    );

    const distanceFactor = getIntensity(
      distance,
      maxVolumeDistance,
      minVolumeDistance,
      1,
      0
    );

    sound.volume(
      getIntensity(distanceFactor, 0, 1, minVolume, maxVolume) *
        masterVolumeRef.current
    );

    lineRef.current?.setFromPoints([
      { x: position[0], y: position[1], z: position[2] },
      playerRef.current.position,
    ]);
    lineMaterialRef.current.opacity = distanceFactor;
    //sound.volume(0);
    /*

    pointsMaterialRef.current.size = getIntensity(
      distanceFactor,
      0,
      1,
      minPointSize,
      maxPointSize
    );

    */

    const scale = getIntensity(distanceFactor, 0, 1, minSize, maxSize);
    pointsRef?.current?.scale.set(scale, scale, scale);

    if (distanceFactor > 0) {
      const rotationSpeed = getIntensity(
        distanceFactor,
        0,
        1,
        minRotationSpeed,
        maxRotationSpeed
      );
      pointsRef?.current?.rotation.set(
        pointsRef.current.rotation.x + rotationSpeed * 0.4,
        pointsRef.current.rotation.y + rotationSpeed,
        pointsRef.current.rotation.z + rotationSpeed * 0.3
      );

      const angle = angleBetweenPoints(
        { x: playerRef.current.position.x, y: playerRef.current.position.z },
        { x: position[0], y: position[2] }
      );

      sound.stereo(panFactor * (Math.abs(angle) / (Math.PI * -0.5) + 1));
    }
  });

  useEffect(() => {}, []);

  return (
    <group>
      <points transparent ref={pointsRef} position={position} scale={[1, 1, 1]}>
        <icosahedronGeometry args={[1, detail - 1]} />
        <pointsMaterial
          size={0.1}
          color={"#ffffff"}
          attach="material"
          ref={pointsMaterialRef}
          opacity={1}
        />
      </points>

      <line>
        <bufferGeometry ref={lineRef} />
        <lineBasicMaterial
          ref={lineMaterialRef}
          color="white"
          transparent
          opacity={0.1}
        />
      </line>
    </group>
  );
}
